import { throwError as observableThrowError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
  AnalyticsData, AnalyticsQuery, GAReporting, ResponseReport
} from 'app/admin-analytics/admin-analytics.datamodel';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'web-common/src/app/cookie.service';
import { environment } from 'environments/environment';

interface GAReportingResponse {
  status: number,
  data: GAReporting
}

interface HistoricalGAResponse {
  status: number,
  data: string
}

@Injectable()
export class AdminAnalyticsService {

  private readonly url = '/api/2/analytics/analytics-*/event/collect';
  constructor(
    private http : HttpClient,
    private cookieService: CookieService) {}

  public search(query : AnalyticsQuery) : Observable < AnalyticsData > {
    return this.http.post(this.url, query)
      .pipe(map(response => <AnalyticsData>response));
  }

  public getGAData(gaFilter: any, metric: string) : Observable < GAReporting > {
    return this.http.post<GAReportingResponse>('/api/2/analytics/reporting/' + metric, gaFilter)
      .pipe(map(function(res: GAReportingResponse) {
        let body = res;
        const result = body.data as GAReporting;
        return result;
      }), catchError((err: any) => {
        return observableThrowError(err.message || err.json());
      }));
  }

  public getGAHistoricalDataUrl() : Observable < string > {
    return this.http.get<HistoricalGAResponse>(environment.rw3apiURL + 'institution/historicalGA/')
        .pipe(map(function(res: HistoricalGAResponse) {
          let body = res;
          return body.data;
        }), catchError(function(err: any) {
          return observableThrowError(err);
        }));
  }

  public getGAHistoricalDataFile(presignedUrl: string, handleError: any) : any {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', presignedUrl, true);
    xhr.responseType = 'blob';

    xhr.addEventListener('error', function throwError(e) {
      handleError('An error occurred when trying to download the file');
    });

    xhr.onload = function() {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'RefWorks-historical-usage.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        handleError(`An error occurred when trying to download the file. Status: ${xhr.status}`);
      }
    };

    xhr.send();
  }

  public getReportData(report, filters) : Observable <ResponseReport> {
    return this.http.post<ResponseReport>(environment.rw3apiURL + 'admin/report/' + report, filters, {headers: this.getHeaders()})
      .pipe(map(function(res: ResponseReport) {
        return res as ResponseReport;
      }), catchError(function(err: any) {
        return observableThrowError(err.json());
      }));
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    let cookieString = 'id=' + this.cookieService.get('id') + '; user_token=' + this.cookieService.get('user_token') + ';';
    headers.append('cookiestring', cookieString);
    headers.append('Content-Type', 'application/json');
    headers.append('apiVersion', '1');
    headers.append('appid', environment.appId);
    return headers;
  }

  public getReportCSV = (
    trend: Trend,
    roles: string[],
    areaOfFocus: string[],
    additionalProfileInformation: string[],
    dateFrom: number,
    dateTo: number,
    endpoint: string
  ): Observable<any> => {

    const requestBody = {
      'trend': trend,
      'roles': roles,
      'areaOfFocus': areaOfFocus,
      'additionalProfileInformation': additionalProfileInformation,
      'dateFrom': dateFrom,
      'dateTo': dateTo
    };

    const headers = this.getHeaders();
    headers.append('Content-Type', 'text/csv; charset=utf-8');
    headers.append('Accept-Charset', 'UTF-8');
    const requestOptions = {
      headers: headers,
      responseType: 'text' as 'json'
    };

    return this.http.post(environment.rw3apiURL + endpoint, requestBody, requestOptions);
  }

  public getAccountsCreatedReportCSV = (
    trend: Trend,
    roles: string[],
    areaOfFocus: string[],
    additionalProfileInformation: string[],
    dateFrom: number,
    dateTo: number
  ): Observable<Blob> => {
    return this.getReportCSV(trend, roles, areaOfFocus, additionalProfileInformation, dateFrom, dateTo, 'admin/report/users/csv/');
  }

  public getReferencesAddedReportCSV = (
    trend: Trend,
    roles: string[],
    areaOfFocus: string[],
    additionalProfileInformation: string[],
    dateFrom: number,
    dateTo: number
  ): Observable<Blob> => {
    return this.getReportCSV(trend, roles, areaOfFocus, additionalProfileInformation, dateFrom, dateTo, 'admin/report/references/csv/');
  }

}

export enum Trend {
  Day = "DAY",
  Month = "MONTH"
}
